import { Box, PageHeader } from '@dagens/carrot';
import { Translation } from '@dagens/frontend-i18n';
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import Input from '../../../_common/components/input/Input.style';
import { Order } from '../../../types/Order';
import { useThrowError } from '../../../utils/errorHandling';
import { ProductList } from './ProductList';
import Container from '@carrot-deprecated/container';
import Typography from '@carrot-deprecated/typography';
import REQ, { combineReqs, ReqType } from 'utils/REQ';
import { findRightConsumerPrice } from 'utils/product-pricing';
import { getOrders, postAddOrderLines } from 'api';
import { Page } from '@components/page';
import { fetchOrders } from '_common/reducers/orders';
import { useAppDispatch, useAppSelector } from '_common/hooks/reduxHooks';
import Button, { ButtonLink } from '_common/components/button/Button.style';

const AddOrderLinePage = () => {
  const [submitReq, setSubmitReq] = useState<ReqType>(REQ.INIT);
  const [filterString, setFilterString] = useState<string>('');
  const [basket, setBasket] = useState<{ productId: string; units: number }[]>(
    []
  );
  const [order, setOrder] = useState<Order>();
  const { secret } = useParams<{ secret: string }>();
  const dispatch = useAppDispatch();
  const throwError = useThrowError();

  const { products, userId } = useAppSelector(
    ({ auth, producerProducts: { items } }) => {
      return {
        products: items,
        userId: auth._id
      };
    }
  );

  const getOrder = async () => {
    try {
      // If the param is the order secret, use getOrders
      const orders = await getOrders({ secret });
      // If no orders are found for the secret, try to see if the param is the order number
      const order =
        orders.at(0) ?? (await getOrders({ orderNumber: secret })).at(0);

      if (order) {
        setOrder(order);
      } else {
        throwError(
          `No orders found for secret ${secret} for producer id ${userId} on add orderline page!`
        );
      }
    } catch (e) {
      throwError(e);
    }
  };

  const onPick = (id: string, units: number | '') => {
    if (units === 0 || units === '') {
      setBasket(
        basket.filter(({ productId }) => {
          return productId !== id;
        })
      );
    } else {
      setBasket([
        ...basket.filter(({ productId }) => {
          return productId !== id;
        }),
        { productId: id, units }
      ]);
    }
  };

  const onSubmit = async () => {
    setSubmitReq(REQ.PENDING);
    const payload = {
      orderId: order?._id,
      basket: basket.map(({ productId, units }) => {
        return { productId, unitsInBasket: units };
      })
    };

    postAddOrderLines(payload).then(() => {
      dispatch(fetchOrders({ producerId: userId }, { clearStore: false }));
      setSubmitReq(REQ.SUCCESS);
    });
  };

  const filteredProducts = products
    .filter(({ prices = [] }) => {
      return !!findRightConsumerPrice(prices, order?.consumer._id);
    })
    .filter(({ name, type = '' }) => {
      return (
        !filterString ||
        name.toLowerCase().includes(filterString.toLowerCase()) ||
        type.toLowerCase().includes(filterString.toLowerCase())
      );
    });

  useEffect(() => {
    getOrder();
  }, [secret]);

  if (submitReq === REQ.SUCCESS) {
    return <Navigate to={`/orders/${secret}`} />;
  }

  if (!order) {
    return null;
  }

  return (
    <Translation>
      {t => {
        return (
          <Page
            header={
              <Box.FullWidth>
                <PageHeader
                  title={t('producer:AddOrderLine')}
                  subtitle={t('common:OrderWithNumber', {
                    orderNumberString: order?.orderNumberString
                  })}
                />
                <Input
                  placeholder={t('producer:SearchInProducts')}
                  value={filterString}
                  onChange={({ target: { value } }) => {
                    return setFilterString(value);
                  }}
                />
              </Box.FullWidth>
            }
            bottom={
              <Box.BottomSheet border>
                <ButtonLink to={`/orders/${secret}`}>
                  {t('common:BackWithArrow')}
                </ButtonLink>
                {(submitReq === REQ.INIT || submitReq === REQ.ERROR) && (
                  <Button
                    disabled={basket.length < 1}
                    onClick={onSubmit}
                    variant="secondary"
                  >
                    {t('common:addLine', {
                      count: basket.length
                    })}
                  </Button>
                )}
                {submitReq === REQ.PENDING && (
                  <Typography variant="paragraph" color="secondary">
                    {t('producer:AddingOrderLines')}
                  </Typography>
                )}
              </Box.BottomSheet>
            }
          >
            <Container mb="xl">
              <ProductList
                items={filteredProducts}
                productsWithUnit={basket}
                onPick={onPick}
                consumerId={order?.consumer._id}
              />
            </Container>
          </Page>
        );
      }}
    </Translation>
  );
};

const AddOrderLinePageFetcher = () => {
  const req = useAppSelector(
    ({ producerProducts: { req }, orders: { req: orderReq } }) => {
      return combineReqs([req, orderReq]);
    }
  );

  if (req !== REQ.SUCCESS) {
    return <Page.Status req={req} />;
  }

  return <AddOrderLinePage />;
};

export default AddOrderLinePageFetcher;
